/**
 * Shapes and vocabularies shared by the identity and access screens.
 *
 * The five things RTCOM keeps apart stay apart here too: a PERSON (core.persons), a USER ACCOUNT
 * (iam.user_accounts), a ROLE, a PERMISSION, and an OPERATIONAL ASSIGNMENT. None of these types merges two
 * of them into a convenient "user" object, because the convenience is exactly what makes "suspend the
 * login but keep the employee" or "this person has no login at all" impossible to express later.
 */
import {
  Ban, CheckCircle2, CircleDashed, Clock, Hourglass, KeyRound, Lock, MailCheck, MailX, ShieldAlert, ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import type { Tone } from '../../ui/primitives';
import { dateTime as fmtDateTime } from '../../ui/format';

export type AccountStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'LOCKED' | 'DEACTIVATED';
export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
export type Sensitivity = 'NORMAL' | 'SENSITIVE' | 'CRITICAL';

interface StatusMeta {
  tone: Tone;
  icon: LucideIcon;
  word: string;
}

/** Tone, icon and word, always all three: colour is never the only signal. */
export const ACCOUNT_STATUS: Record<AccountStatus, StatusMeta> = {
  INVITED: { tone: 'queued', icon: Hourglass, word: 'Invited' },
  ACTIVE: { tone: 'accepted', icon: CheckCircle2, word: 'Active' },
  SUSPENDED: { tone: 'refused', icon: Ban, word: 'Suspended' },
  LOCKED: { tone: 'refused', icon: Lock, word: 'Locked' },
  DEACTIVATED: { tone: 'neutral', icon: CircleDashed, word: 'Deactivated' },
};

export const INVITATION_STATUS: Record<InvitationStatus, StatusMeta> = {
  PENDING: { tone: 'queued', icon: Clock, word: 'Pending' },
  ACCEPTED: { tone: 'accepted', icon: MailCheck, word: 'Accepted' },
  EXPIRED: { tone: 'neutral', icon: CircleDashed, word: 'Expired' },
  REVOKED: { tone: 'refused', icon: MailX, word: 'Revoked' },
};

export const SENSITIVITY: Record<Sensitivity, StatusMeta> = {
  NORMAL: { tone: 'neutral', icon: KeyRound, word: 'Normal' },
  SENSITIVE: { tone: 'queued', icon: ShieldCheck, word: 'Sensitive' },
  CRITICAL: { tone: 'refused', icon: ShieldAlert, word: 'Critical' },
};

export interface UserRow {
  id: string;
  email: string;
  status: AccountStatus;
  mfa_required: boolean;
  last_sign_in_at: string | null;
  person_id: string | null;
  person_name: string | null;
  created_at: string;
  updated_at: string;
  deactivated_at: string | null;
  signs_in: boolean;
  role_count: number;
}

export interface InvitationRow {
  id: string;
  email: string;
  person_id: string | null;
  person_name: string | null;
  proposed_roles: string[];
  status: InvitationStatus;
  expires_at: string;
  is_overdue: boolean;
  invited_by_email: string | null;
  accepted_at: string | null;
  created_at: string;
}

export interface RoleRow {
  id: string;
  code: string;
  name: string;
  role_group: string;
  description: string | null;
  external_role_key: string | null;
  requires_mfa: boolean;
  is_system: boolean;
  status: 'ACTIVE' | 'RETIRED';
  permission_count: number;
}

export interface PermissionRow {
  code: string;
  module: string;
  description: string;
  sensitivity: Sensitivity;
}

export interface GrantView {
  id: string;
  role_code: string;
  role_name: string;
  role_is_system: boolean;
  role_requires_mfa: boolean;
  scope: 'ORGANIZATION' | 'LOCATIONS';
  location_ids: string[];
  locations: { id: string; code: string; name: string; kind: string }[];
  legal_entity_id: string | null;
  valid_from: string;
  valid_to: string | null;
  state: 'CURRENT' | 'SCHEDULED' | 'ENDED' | 'ROLE_RETIRED';
  reason: string | null;
  granted_by: string | null;
}

/** Exactly what `api.effective_access` returns. */
export interface EffectiveAccess {
  account: {
    id: string;
    email: string;
    status: AccountStatus;
    mfa_required: boolean;
    person_id: string | null;
    person_name: string | null;
    last_sign_in_at: string | null;
    signs_in: boolean;
  };
  effective: boolean;
  blocked_by: string | null;
  mfa: { account_requires: boolean; roles_requiring: string[]; finding: string | null };
  grants: GrantView[];
  permissions: {
    code: string;
    module: string;
    sensitivity: Sensitivity;
    via: string[];
    organization_wide: boolean;
    location_ids: string[];
  }[];
  applications: {
    code: string; name: string; owner_system: string; status: string; authority: 'ERP' | 'MIRROR';
    external_role_key: string | null;
  }[];
  at_location: {
    location: { id: string; code: string; name: string; kind: string } | null;
    inherits_from: { id: string; code: string; depth: number }[];
    allowed: string[];
    withheld: string[];
  } | null;
  counts: { grants: number; permissions: number; applications: number };
}

export type AccessReason =
  | 'ALLOWED'
  | 'NO_SUCH_PERMISSION'
  | 'ACCOUNT_NOT_ACTIVE'
  | 'NO_ERP_ACCESS'
  | 'NO_ROLE_WITH_PERMISSION'
  | 'GRANT_NOT_CURRENT'
  | 'OUT_OF_SCOPE';

/** Exactly what `api.access_check` returns. */
export interface AccessCheck {
  permission: string;
  allowed: boolean;
  reason: AccessReason;
  detail: string;
  nearest?: string[];
  roles_that_would?: string[];
  covers?: { id: string; code: string; name: string }[];
  requires_mfa_session?: boolean;
  grants?: { grant_id: string; role: string; scope: string; current: boolean; valid_to: string | null }[];
}

/** PostgREST's `or=()` filter is comma- and parenthesis-delimited, so those must not come from a search box. */
export function safeTerm(raw: string): string {
  return raw.replace(/[,()%*\\]/g, ' ').trim();
}

export function when(value: string | null | undefined): string | null {
  return value ? fmtDateTime(value) : null;
}
